/**
 * WI-workos (N1/N2) — a MOCK WorkOS surface + fixture tokens. This is the
 * fixture leg that carries the DRY halves of AC-workos-01/03/04/08/10; the live
 * WorkOS environment legs are GATED §S8 and are NOT exercised here.
 *
 * The mock models exactly the WorkOS primitives the increment consumes, no more:
 *   - Organizations (one per tenant — architecture §2)
 *   - Memberships (user ↔ org, carrying a role — the ONE primitive, §2)
 *   - AuthKit login → an OIDC-shaped token IFF the user has a membership
 *
 * "Fixture token" is a compact, verifiable stand-in for a WorkOS-issued OIDC
 * JWT: a base64url header.payload.signature triple signed with a FIXTURE HMAC
 * key. The verifier (WI-workos-07) pins the JWKS/issuer source to this WorkOS
 * fixture key — DISTINCT from the deferred Plane-2 oidc.<apex> bucket
 * (PIN-workos-B). This is the minimal-for-fixture resolution of DS-workos-03's
 * token surface; it pre-decides NO middleware framework.
 */

import { WORKOS_ISSUER } from "./workos-config";

/** The three roles WorkOS stamps into the membership claim (architecture §3). */
export type WorkosRole = "member" | "supervisor" | "admin";

/** A WorkOS Organization — one per tenant (architecture §2). `org_id` is the
 * stable id the app resolves a subdomain to (WI-workos-02). */
export interface WorkosOrg {
  readonly org_id: string;
  /** The tenant slug this org belongs to (`mercer`, `gc-acme`, …). One org per
   * tenant; two tenants NEVER share an org (TW-workos-02). `readonly`: an org's
   * owning tenant can never be rebound after construction — the only way to set
   * it is `createOrg`, which is collision-guarded both directions. */
  readonly tenant: string;
  /** Self-serve email-domain auto-join toggle (WI-workos-06). Default OFF —
   * provisioning (invite → membership) is the default path (DEC-0002). Mutated
   * only through `configureSelfServe` on the stored record, never on an escaped
   * copy. */
  self_serve_domain: string | null;
}

/** A WorkOS membership: the ONE primitive binding a user to an org with a role
 * (architecture §2; DEC-0002 §Decision(1)). Revoking it removes all access.
 * `user_id`/`org_id` are `readonly`: a membership can never be re-keyed to a
 * different user or org after construction (that would move identity across the
 * tenant boundary — TW-workos-02). Only `role` is mutable, via
 * `createMembership`'s add-or-update on the stored record. */
export interface WorkosMembership {
  readonly user_id: string;
  readonly org_id: string;
  role: WorkosRole;
}

/** The claim set WorkOS stamps into an AuthKit-issued OIDC token
 * (architecture §1 Plane 1: `iss=WorkOS, org_id, role`). `iat`/`exp` are the
 * standard OIDC lifetime claims (seconds since epoch); the verifier
 * (WI-workos-07) REJECTS a token whose `exp` is at/before the verification
 * clock, so a captured/replayed expired token can never establish a session. */
export interface WorkosClaims {
  iss: string;
  sub: string;
  org_id: string;
  role: WorkosRole;
  iat: number;
  exp: number;
}

/** Default fixture token lifetime (seconds) — AuthKit access tokens are
 * short-lived; the human re-auths through the hosted flow when one expires. */
export const FIXTURE_TOKEN_TTL_SECONDS = 3600;

// --- fixture signing (a WorkOS-published-JWKS stand-in) -----------------------

/** The fixture HMAC "JWKS" key — the WorkOS-side signer stand-in. Obviously a
 * TEST value (never a real WorkOS secret; passes the WI-workos-10 scan). The
 * verifier pins to THIS key (WI-workos-07 / PIN-workos-B), NOT the deferred
 * Plane-2 oidc.<apex> bucket. */
export const WORKOS_FIXTURE_JWKS_KEY = "workos_fixture_jwks_key_FAKE_not_a_secret";

function b64url(input: string): string {
  return btoa(input).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Deterministic non-cryptographic HMAC stand-in over `${header}.${payload}`
 * with a key — enough for a fixture to prove "signed by the pinned WorkOS key"
 * vs "tampered / wrong key". NOT real crypto; the live leg uses WorkOS's real
 * JWKS (GATED §S8). */
export function fixtureSign(signingInput: string, key: string): string {
  let h = 2166136261 >>> 0;
  const material = `${key}::${signingInput}`;
  for (let i = 0; i < material.length; i++) {
    h ^= material.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return b64url(`sig_${h.toString(16)}`);
}

/** Encode a WorkOS-shaped fixture token (header.payload.signature). Signed with
 * the pinned WORKOS_FIXTURE_JWKS_KEY unless a different key is passed (used by
 * tests to forge a wrong-signer token the verifier must reject). `iat`/`exp` are
 * defaulted to a live 1h window when omitted, so a fixture caller gets a valid,
 * unexpired token by default; a test proving the expiry guard passes an explicit
 * past `exp`. */
export function encodeFixtureToken(
  claims: Omit<WorkosClaims, "iat" | "exp"> & Partial<Pick<WorkosClaims, "iat" | "exp">>,
  key: string = WORKOS_FIXTURE_JWKS_KEY,
): string {
  const iat = claims.iat ?? Math.floor(Date.now() / 1000);
  const exp = claims.exp ?? iat + FIXTURE_TOKEN_TTL_SECONDS;
  const full: WorkosClaims = { ...claims, iat, exp };
  const header = b64url(JSON.stringify({ alg: "HS256", typ: "JWT", kid: "workos-fixture" }));
  const payload = b64url(JSON.stringify(full));
  const signature = fixtureSign(`${header}.${payload}`, key);
  return `${header}.${payload}.${signature}`;
}

// --- the mock WorkOS directory + AuthKit -------------------------------------

/** An in-memory mock of the WorkOS directory (orgs + memberships) and its
 * AuthKit login. This is the fixture WorkOS "environment". */
export class MockWorkos {
  private orgs = new Map<string, WorkosOrg>();
  private memberships: WorkosMembership[] = [];

  /** Return a frozen shallow copy so a stored org/membership NEVER escapes as a
   * live mutable reference. Even a caller who casts away `readonly` mutates a
   * throwaway copy, not the record in `orgs`/`memberships` — so an org's owning
   * tenant and a membership's user/org keys cannot be rebound post-construction
   * to slip past the createOrg collision guard (TW-workos-02, the cross-org
   * isolation invariant). Internal reads use the stored objects directly. */
  private static snapshotOrg(o: WorkosOrg): WorkosOrg {
    return Object.freeze({ ...o });
  }
  private static snapshotMembership(m: WorkosMembership): WorkosMembership {
    return Object.freeze({ ...m });
  }

  /** Create one Organization for a tenant (architecture §2/§4). Refuses a
   * second org for the same tenant — one org per tenant (TW-workos-02) — AND
   * refuses to re-create an org_id that already exists. An org_id is a
   * WorkOS-global identifier; silently overwriting it would rebind an existing
   * org (and its memberships) to a different tenant, letting tenant A's members
   * become valid identity in tenant B. Both directions of the bijection are
   * fail-closed. */
  createOrg(org_id: string, tenant: string, self_serve_domain: string | null = null): WorkosOrg {
    if (this.orgs.has(org_id)) {
      const owner = this.orgs.get(org_id)!;
      throw new Error(
        `org-id collision: org '${org_id}' already exists (tenant '${owner.tenant}') and cannot be rebound to tenant '${tenant}'`,
      );
    }
    for (const o of this.orgs.values()) {
      if (o.tenant === tenant) {
        throw new Error(`org-per-tenant violation: tenant '${tenant}' already has org '${o.org_id}'`);
      }
    }
    const org: WorkosOrg = { org_id, tenant, self_serve_domain };
    this.orgs.set(org_id, org);
    return MockWorkos.snapshotOrg(org);
  }

  getOrg(org_id: string): WorkosOrg | undefined {
    const org = this.orgs.get(org_id);
    return org && MockWorkos.snapshotOrg(org);
  }

  /** Flip the per-tenant self-serve domain. This changes directory config only;
   * `authkitLogin` continues to mint auto-joins through createMembership, the
   * exact same primitive used by provisioned onboarding (WI-workos-06). */
  configureSelfServe(org_id: string, email_domain: string | null): WorkosOrg {
    const org = this.orgs.get(org_id);
    if (!org) {
      throw new Error(`cannot configure self-serve: org '${org_id}' does not exist`);
    }
    org.self_serve_domain = email_domain;
    return MockWorkos.snapshotOrg(org);
  }

  /** ONE primitive — add: create a membership WITH a role in an org
   * (AC-workos-03). Refuses a membership into a non-existent org and refuses to
   * target an org that is not the intended one (the caller passes org_id; the
   * mock never infers it). */
  createMembership(user_id: string, org_id: string, role: WorkosRole): WorkosMembership {
    if (!this.orgs.has(org_id)) {
      throw new Error(`cannot create membership: org '${org_id}' does not exist`);
    }
    const existing = this.memberships.find((m) => m.user_id === user_id && m.org_id === org_id);
    if (existing) {
      existing.role = role;
      return MockWorkos.snapshotMembership(existing);
    }
    const m: WorkosMembership = { user_id, org_id, role };
    this.memberships.push(m);
    return MockWorkos.snapshotMembership(m);
  }

  /** ONE primitive — remove: revoke the membership (AC-workos-04). After it, the
   * user can obtain no token for that org (see `authkitLogin`). */
  revokeMembership(user_id: string, org_id: string): void {
    this.memberships = this.memberships.filter((m) => !(m.user_id === user_id && m.org_id === org_id));
  }

  membershipOf(user_id: string, org_id: string): WorkosMembership | undefined {
    const m = this.memberships.find((m) => m.user_id === user_id && m.org_id === org_id);
    return m && MockWorkos.snapshotMembership(m);
  }

  /** AuthKit hosted login (fixture). Returns a WorkOS-shaped token IFF the user
   * has a membership in the target org (architecture §2 "Hard isolation": no
   * membership ⇒ no token ⇒ no access). Returns null otherwise — the
   * AC-workos-01 / AC-workos-04 negative.
   *
   * `email_domain` supports the self-serve auto-join path (WI-workos-06): if the
   * org has self-serve enabled and the login email domain matches, a `member`
   * membership is minted via the SAME createMembership primitive (no second
   * onboarding path), then the login proceeds. */
  authkitLogin(user_id: string, org_id: string, email_domain?: string, nowSeconds: number = Math.floor(Date.now() / 1000)): string | null {
    const org = this.orgs.get(org_id);
    if (!org) return null;

    let membership = this.membershipOf(user_id, org_id);
    if (!membership && org.self_serve_domain && email_domain === org.self_serve_domain) {
      // Self-serve auto-join → default `member`, atop the SAME primitive.
      membership = this.createMembership(user_id, org_id, "member");
    }
    if (!membership) return null;

    return encodeFixtureToken({
      iss: WORKOS_ISSUER,
      sub: user_id,
      org_id,
      role: membership.role,
      iat: nowSeconds,
      exp: nowSeconds + FIXTURE_TOKEN_TTL_SECONDS,
    });
  }
}
